﻿import {hasFlag, isNullOrEmpty, StringNamedValue, translation} from "@2d-front-monorepo/core";
import {IMvvmContextState, MvvmSelect, MvvmTextInput} from "@2d-front-monorepo/design-library";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {JSX, useMemo} from "react";
import AttachmentType from "../enums/AttachmentType";
import RequiredVehicleProperties from "../installations/RequiredVehicleProperties";
import ICreateComponentAttachments from "../interfaces/ICreateComponentAttachments";
import {CreateVehicleArguments} from "../types/arguments/CreateVehicleArguments";
import {ComponentWithChildrenParts} from "./ComponentParts";
import CreateAttachmentsComponent from "./CreateAttachmentsComponent";

export interface CreateVehicleComponentProps extends ComponentWithChildrenParts {
	context: IMvvmContextState<CreateVehicleModel>;
	plateValidation: PlateValidationRequirements;

	colors: StringNamedValue[];
	models: StringNamedValue[];
	provinces: StringNamedValue[];

	requiredProperties: RequiredVehicleProperties;
	readOnlyProperties?: RequiredVehicleProperties;
	hiddenProperties?: RequiredVehicleProperties;

	requiredAttachments?: AttachmentType[];
	optionalAttachments?: AttachmentType[];
}

export type PlateValidationRequirements = {
	maxLength: number;
	existing: string[];
}

export type CreateVehicleModel = {
	arguments: CreateVehicleArguments;
	customData: Record<string, any>;
} & ICreateComponentAttachments;

export default function CreateVehicleComponent(props: CreateVehicleComponentProps): JSX.Element {
	const {
		context,
		requiredProperties,
		readOnlyProperties,
		hiddenProperties,
		requiredAttachments,
		optionalAttachments,
		plateValidation,
		colors,
		models,
		provinces,
		header,
		children,
		footer
	} = props;

	const roProperties = readOnlyProperties ? readOnlyProperties : RequiredVehicleProperties.None;
	const hProperties = hiddenProperties ? hiddenProperties : RequiredVehicleProperties.None;

	const plateValidationObject = useMemo(() => {
		return {
			required: hasFlag(requiredProperties, RequiredVehicleProperties.Plate) ? translation(PermitTranslations.validation__vehicle__plate__required) : undefined,
			validate: (plate: string | undefined) => {
				if (isNullOrEmpty(plate)) return undefined;


				if (plateValidation.maxLength > 0 && plate.length > plateValidation.maxLength) {
					return translation(PermitTranslations.validation__vehicle__plate__max_length, {value: plateValidation.maxLength});
				}

				const upperPlate = plate.toUpperCase();
				return  plateValidation.existing.find(x => x === upperPlate)
					? translation(PermitTranslations.validation__vehicle__plate__duplicated, {value: upperPlate})
					: undefined;
			}
		};
	}, [requiredProperties]);

	const nickNameValidation = useMemo(() => ({
		required: hasFlag(requiredProperties, RequiredVehicleProperties.Nickname) ? translation(PermitTranslations.validation__vehicle__nickname__required) : undefined
	}), [requiredProperties]);

	const colorValidation = useMemo(() => ({
		required: hasFlag(requiredProperties, RequiredVehicleProperties.Color) ? translation(PermitTranslations.validation__vehicle__color__required) : undefined
	}), [requiredProperties]);

	const modelValidation = useMemo(() => ({
		required: hasFlag(requiredProperties, RequiredVehicleProperties.Model) ? translation(PermitTranslations.validation__vehicle__model__required) : undefined
	}), [requiredProperties]);

	const typeValidation = useMemo(() => ({
		required: hasFlag(requiredProperties, RequiredVehicleProperties.Type) ? translation(PermitTranslations.validation__vehicle__type__required) : undefined
	}), [requiredProperties]);

	const provinceValidation = useMemo(() => ({
		required: hasFlag(requiredProperties, RequiredVehicleProperties.Province) ? translation(PermitTranslations.validation__vehicle__province__required) : undefined
	}), [requiredProperties]);

	return (<div className="layout vertical gap">
		{header}
		<div className="columns gap">
			<MvvmTextInput context={context}
			               property="arguments.plate"
			               type="text"
			               column="col25 vehicle--plate"
			               autoFocus
			               disabled={hasFlag(roProperties, RequiredVehicleProperties.Plate)}
			               hidden={hasFlag(hProperties, RequiredVehicleProperties.Plate)}
			               maxLength={plateValidation.maxLength}
			               validation={plateValidationObject}
			               label={translation(PermitTranslations.new_vehicle__plate)}/>

			<MvvmTextInput context={context}
			               property="arguments.data.nickname"
			               type="text"
			               column="col25"
			               disabled={hasFlag(roProperties, RequiredVehicleProperties.Nickname)}
			               hidden={hasFlag(hProperties, RequiredVehicleProperties.Nickname)}
			               validation={nickNameValidation}
			               label={translation(PermitTranslations.new_vehicle__nickname)}/>

			<MvvmSelect context={context}
			            property="arguments.data.color"
			            items={colors}
			            placeholder={translation(PermitTranslations.vehicle__color__placeholder)}
			            column="col25"
			            disabled={hasFlag(roProperties, RequiredVehicleProperties.Color)}
			            hidden={hasFlag(hProperties, RequiredVehicleProperties.Color)}
			            validation={colorValidation}
			            label={translation(PermitTranslations.new_vehicle__color)}/>

			<MvvmSelect context={context}
			            property="arguments.data.model"
			            items={models}
			            placeholder={translation(PermitTranslations.vehicle__model__placeholder)}
			            column="col25"
			            disabled={hasFlag(roProperties, RequiredVehicleProperties.Model)}
			            hidden={hasFlag(hProperties, RequiredVehicleProperties.Model)}
			            validation={modelValidation}
			            label={translation(PermitTranslations.new_vehicle__model)}/>

			<MvvmTextInput context={context}
			               property="arguments.data.type"
			               type="text"
			               inputMode="numeric"
			               column="col25"
			               placeholder="YYYY"
			               disabled={hasFlag(roProperties, RequiredVehicleProperties.Type)}
			               hidden={hasFlag(hProperties, RequiredVehicleProperties.Type)}
			               validation={typeValidation}
			               label={translation(PermitTranslations.new_vehicle__type)}/>

			<MvvmSelect context={context}
			            property="arguments.data.province"
			            items={provinces}
			            placeholder={translation(PermitTranslations.vehicle__province__placeholder)}
			            column="col25"
			            disabled={hasFlag(roProperties, RequiredVehicleProperties.Province)}
			            hidden={hasFlag(hProperties, RequiredVehicleProperties.Province)}
			            validation={provinceValidation}
			            label={translation(PermitTranslations.new_vehicle__province)}/>
		</div>
		{children}
		<CreateAttachmentsComponent context={context}
		                            label={translation(PermitTranslations.documentation__required)}
		                            types={requiredAttachments}
		                            required={true}/>

		<CreateAttachmentsComponent context={context}
		                            label={translation(PermitTranslations.documentation__additional)}
		                            types={optionalAttachments}
		                            required={false}/>
		{footer}
	</div>);
}
